import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Card,
    CardActions,
    Grid,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    CardContent
} from '@mui/material';
import {
    PhotoCamera as PhotoCameraIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Collections as CollectionsIcon
} from '@mui/icons-material';
import { filesApi, projectFormsApi } from '../services/api';
import { compressImage } from '../utils/imageCompression';
import { renameFile } from '../utils/fileNaming';
import { limitFilesForUpload } from '../utils/uploadLimits';
import ImageViewer from './ImageViewer';

const PhotoUploadField = ({ 
    field, 
    value = [], 
    onChange, 
    projectId, 
    formId, 
    disabled = false 
}) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [photoUrls, setPhotoUrls] = useState({});
    const [photoPreview, setPhotoPreview] = useState({ open: false, url: '', title: '', allPhotos: [], currentIndex: 0 });
    const [documentPickerOpen, setDocumentPickerOpen] = useState(false);
    const [documentPhotos, setDocumentPhotos] = useState([]);
    const [documentPhotoUrls, setDocumentPhotoUrls] = useState({});
    const [selectedDocumentPhotoIds, setSelectedDocumentPhotoIds] = useState([]);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [linkingDocuments, setLinkingDocuments] = useState(false);
    const fileInputRef = useRef();
    const isPersistedPhotoId = (photoId) => /^\d+$/.test(String(photoId || ''));
    const resolveInlinePhotoUrl = (photo) => {
        if (!photo) return '';
        if (typeof photo.url === 'string' && photo.url) return photo.url;
        if (typeof photo.blob_path === 'string' && /^https?:\/\//.test(photo.blob_path)) return photo.blob_path;
        return '';
    };

    // Load photo URLs when photos change
    useEffect(() => {
        const loadPhotoUrls = async () => {
            if (!value || value.length === 0) return;
            
            const newUrls = {};
            await Promise.all(
                value.map(async (photo) => {
                    const inlineUrl = resolveInlinePhotoUrl(photo);
                    if (inlineUrl) {
                        newUrls[photo.photo_id] = inlineUrl;
                        return;
                    }

                    if (isPersistedPhotoId(photo.photo_id)) {
                        try {
                            const response = await projectFormsApi.getPhotoUrl(photo.photo_id);
                            newUrls[photo.photo_id] = response.data.url;
                        } catch (err) {
                            console.error(`Failed to load URL for photo ${photo.photo_id}:`, err);
                        }
                    }
                })
            );
            
            if (Object.keys(newUrls).length > 0) {
                setPhotoUrls(newUrls);
            }
        };

        loadPhotoUrls();
    }, [value]);

    const handleFileSelect = async (event) => {
        const { files: selectedFiles, warning } = limitFilesForUpload(event.target.files || []);
        if (warning) {
            setError(warning);
            return;
        }

        if (selectedFiles.length === 0) return;

        const invalidFiles = selectedFiles.filter((file) => !file.type.startsWith('image/') || file.size > 10 * 1024 * 1024);
        const validFiles = selectedFiles.filter((file) => file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024);

        if (validFiles.length === 0) {
            setError(invalidFiles.some((file) => file.size > 10 * 1024 * 1024)
                ? 'Image files must be under 10MB'
                : 'Please select image files');
            return;
        }

        try {
            setUploading(true);
            setError(invalidFiles.length > 0 ? 'Some files were skipped. Only images under 10MB are supported.' : '');

            const uploadedPhotos = await Promise.all(validFiles.map(async (file) => {
                const compressedFile = await compressImage(file);
                const renamedFile = renameFile(compressedFile);
                const formData = new FormData();
                formData.append('file', renamedFile);
                formData.append('field_id', field.id);

                const response = await projectFormsApi.uploadPhoto(projectId, formId, formData);
                return {
                    photo_id: response.data.photo_id,
                    file_id: response.data.file_id,
                    file_name: response.data.file_name,
                    field_id: response.data.field_id
                };
            }));

            const updatedPhotos = [...(value || []), ...uploadedPhotos];
            onChange(field.id, updatedPhotos);

            const loadedUrls = {};
            await Promise.all(uploadedPhotos.map(async (photo) => {
                try {
                    const urlResponse = await projectFormsApi.getPhotoUrl(photo.photo_id);
                    loadedUrls[photo.photo_id] = urlResponse.data.url;
                } catch (urlErr) {
                    console.error('Failed to load URL for new photo:', urlErr);
                }
            }));

            if (Object.keys(loadedUrls).length > 0) {
                setPhotoUrls(prev => ({
                    ...prev,
                    ...loadedUrls
                }));
            }

        } catch (err) {
            console.error('Photo upload error:', err);
            setError(err.response?.data?.error || 'Failed to upload photo');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const openDocumentPicker = async () => {
        setDocumentPickerOpen(true);
        setSelectedDocumentPhotoIds([]);
        setLoadingDocuments(true);
        setError('');

        try {
            const response = await filesApi.getByProjectId(projectId, 'photos');
            const imageFiles = (response.data || []).filter((file) => (
                /\.(jpe?g|png|gif|bmp|webp|heic|heif)$/i.test(file.file_name || '')
            ));
            setDocumentPhotos(imageFiles);

            const urlEntries = await Promise.all(imageFiles.map(async (file) => {
                try {
                    const urlResponse = await filesApi.getUrl(file.file_id);
                    return [file.file_id, urlResponse.data.url];
                } catch (urlError) {
                    console.error(`Failed to load document photo ${file.file_id}:`, urlError);
                    return [file.file_id, ''];
                }
            }));
            setDocumentPhotoUrls(Object.fromEntries(urlEntries));
        } catch (err) {
            console.error('Failed to load project photos:', err);
            setError(err.response?.data?.error || 'Failed to load photos from Documents');
            setDocumentPickerOpen(false);
        } finally {
            setLoadingDocuments(false);
        }
    };

    const toggleDocumentPhoto = (fileId) => {
        setSelectedDocumentPhotoIds((current) => (
            current.includes(fileId)
                ? current.filter((id) => id !== fileId)
                : [...current, fileId]
        ));
    };

    const addSelectedDocumentPhotos = async () => {
        if (selectedDocumentPhotoIds.length === 0) return;

        try {
            setLinkingDocuments(true);
            const linkedPhotos = await Promise.all(selectedDocumentPhotoIds.map(async (fileId) => {
                const response = await projectFormsApi.linkPhoto(projectId, formId, {
                    file_id: fileId,
                    field_id: field.id,
                });
                return response.data;
            }));

            const existingPhotoIds = new Set((value || []).map((photo) => String(photo.photo_id)));
            const newPhotos = linkedPhotos.filter((photo) => !existingPhotoIds.has(String(photo.photo_id)));
            onChange(field.id, [...(value || []), ...newPhotos]);

            setPhotoUrls((current) => {
                const next = { ...current };
                newPhotos.forEach((photo) => {
                    const documentUrl = documentPhotoUrls[photo.file_id];
                    if (documentUrl) next[photo.photo_id] = documentUrl;
                });
                return next;
            });
            setDocumentPickerOpen(false);
            setSelectedDocumentPhotoIds([]);
        } catch (err) {
            console.error('Failed to add document photos to form:', err);
            setError(err.response?.data?.error || 'Failed to add selected photos to the form');
        } finally {
            setLinkingDocuments(false);
        }
    };

    const handleDeletePhoto = async (photoId) => {
        try {
            await projectFormsApi.deletePhoto(photoId);
            
            // Remove the photo from the current value
            const updatedPhotos = (value || []).filter(photo => photo.photo_id !== photoId);
            onChange(field.id, updatedPhotos);
            
        } catch (err) {
            console.error('Photo delete error:', err);
            setError(err.response?.data?.error || 'Failed to delete photo');
        }
    };

    const handleViewPhoto = async (photoId) => {
        try {
            // Find the photo and its index
            const photos = value || [];
            const photoIndex = photos.findIndex(p => p.photo_id === photoId);
            const photo = photos[photoIndex];
            
            if (!photo) return;
            
            // Use cached URL if available, otherwise fetch it
            let photoUrl = photoUrls[photoId] || resolveInlinePhotoUrl(photo);
            if (!photoUrl) {
                if (!isPersistedPhotoId(photoId)) {
                    setError('This photo is still syncing and cannot be viewed yet.');
                    return;
                }
                const response = await projectFormsApi.getPhotoUrl(photoId);
                photoUrl = response.data.url;
                setPhotoUrls(prev => ({ ...prev, [photoId]: photoUrl }));
            }
            
            // Open in ImageViewer
            setPhotoPreview({
                open: true,
                url: photoUrl,
                title: photo.file_name || 'Photo',
                allPhotos: photos,
                currentIndex: photoIndex
            });
        } catch (err) {
            console.error('Photo view error:', err);
            setError(err.response?.data?.error || 'Failed to view photo');
        }
    };

    const handlePhotoNavigate = async (direction) => {
        if (!photoPreview.allPhotos || photoPreview.allPhotos.length === 0) return;
        
        let newIndex;
        if (direction === 'next') {
            newIndex = (photoPreview.currentIndex + 1) % photoPreview.allPhotos.length;
        } else {
            newIndex = (photoPreview.currentIndex - 1 + photoPreview.allPhotos.length) % photoPreview.allPhotos.length;
        }
        
        const newPhoto = photoPreview.allPhotos[newIndex];
        let photoUrl = photoUrls[newPhoto.photo_id] || resolveInlinePhotoUrl(newPhoto);

        if (!photoUrl && isPersistedPhotoId(newPhoto.photo_id)) {
            try {
                const response = await projectFormsApi.getPhotoUrl(newPhoto.photo_id);
                photoUrl = response.data.url;
                setPhotoUrls(prev => ({ ...prev, [newPhoto.photo_id]: photoUrl }));
            } catch (err) {
                console.error('Failed to load URL while navigating form photo preview:', err);
            }
        }
        
        setPhotoPreview(prev => ({
            ...prev,
            url: photoUrl || prev.url,
            title: newPhoto.file_name || 'Photo',
            currentIndex: newIndex
        }));
    };

    const photos = value || [];

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    disabled={disabled || uploading}
                />
                
                <Button
                    variant="outlined"
                    startIcon={uploading ? <CircularProgress size={20} /> : <PhotoCameraIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || uploading}
                >
                    {uploading ? 'Uploading...' : 'Take Photos / Upload Images'}
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<CollectionsIcon />}
                    onClick={openDocumentPicker}
                    disabled={disabled || uploading}
                >
                    Choose from Documents
                </Button>
            </Box>

            {photos.length > 0 && (
                <Grid container spacing={2}>
                    {photos.map((photo) => (
                        <Grid item xs={6} sm={4} md={3} key={photo.photo_id}>
                            <Card>
                                <Box sx={{ 
                                    height: 120, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    bgcolor: 'grey.100',
                                    overflow: 'hidden',
                                    cursor: 'pointer'
                                }}
                                onClick={() => handleViewPhoto(photo.photo_id)}
                                >
                                    {(photoUrls[photo.photo_id] || resolveInlinePhotoUrl(photo)) ? (
                                        <img 
                                            src={photoUrls[photo.photo_id] || resolveInlinePhotoUrl(photo)} 
                                            alt={photo.file_name}
                                            style={{ 
                                                width: '100%', 
                                                height: '100%', 
                                                objectFit: 'cover' 
                                            }} 
                                        />
                                    ) : (
                                        <PhotoCameraIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                                    )}
                                </Box>
                                <CardActions>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleViewPhoto(photo.photo_id)}
                                        title="View photo"
                                    >
                                        <VisibilityIcon />
                                    </IconButton>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleDeletePhoto(photo.photo_id)}
                                        disabled={disabled}
                                        color="error"
                                        title="Delete photo"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </CardActions>
                                <Typography variant="caption" sx={{ p: 1, wordBreak: 'break-all' }}>
                                    {photo.file_name}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {photos.length === 0 && (
                <Box sx={{ 
                    border: '2px dashed',
                    borderColor: 'divider', 
                    height: 120, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: 'grey.50',
                    borderRadius: 1
                }}>
                    <Typography color="text.secondary">
                        No photos uploaded yet
                    </Typography>
                </Box>
            )}

            {/* Photo Preview using ImageViewer */}
            <ImageViewer
                open={photoPreview.open}
                onClose={() => setPhotoPreview({ open: false, url: '', title: '', allPhotos: [], currentIndex: 0 })}
                imageUrl={photoPreview.url}
                fileName={photoPreview.title}
                allImages={photoPreview.allPhotos || []}
                currentIndex={photoPreview.currentIndex || 0}
                onNavigate={handlePhotoNavigate}
            />

            <Dialog
                open={documentPickerOpen}
                onClose={() => !linkingDocuments && setDocumentPickerOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Choose photos from Documents</DialogTitle>
                <DialogContent dividers>
                    {loadingDocuments ? (
                        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : documentPhotos.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                            No photos have been uploaded to Documents yet.
                        </Typography>
                    ) : (
                        <Grid container spacing={2}>
                            {documentPhotos.map((file) => {
                                const alreadyAdded = (value || []).some((photo) => photo.file_id === file.file_id);
                                const selected = selectedDocumentPhotoIds.includes(file.file_id);
                                return (
                                    <Grid item xs={6} sm={4} md={3} key={file.file_id}>
                                        <Card
                                            variant="outlined"
                                            onClick={() => !alreadyAdded && toggleDocumentPhoto(file.file_id)}
                                            sx={{
                                                height: '100%',
                                                cursor: alreadyAdded ? 'default' : 'pointer',
                                                borderColor: selected ? 'primary.main' : 'divider',
                                                opacity: alreadyAdded ? 0.55 : 1,
                                            }}
                                        >
                                            <Box sx={{ height: 110, bgcolor: 'grey.100', position: 'relative' }}>
                                                {documentPhotoUrls[file.file_id] ? (
                                                    <img
                                                        src={documentPhotoUrls[file.file_id]}
                                                        alt={file.file_name}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <PhotoCameraIcon color="disabled" />
                                                    </Box>
                                                )}
                                                <Checkbox
                                                    checked={selected || alreadyAdded}
                                                    disabled={alreadyAdded}
                                                    onChange={() => toggleDocumentPhoto(file.file_id)}
                                                    onClick={(event) => event.stopPropagation()}
                                                    inputProps={{ 'aria-label': `Select ${file.file_name}` }}
                                                    sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'background.paper', p: 0.25 }}
                                                />
                                            </Box>
                                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                                <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-word' }}>
                                                    {file.file_name}
                                                </Typography>
                                                {alreadyAdded && (
                                                    <Typography variant="caption" color="text.secondary">Already added</Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDocumentPickerOpen(false)} disabled={linkingDocuments}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={addSelectedDocumentPhotos}
                        disabled={selectedDocumentPhotoIds.length === 0 || linkingDocuments}
                    >
                        {linkingDocuments ? 'Adding...' : `Add selected (${selectedDocumentPhotoIds.length})`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PhotoUploadField;
